<script setup lang="ts">
/**
 * Строка ленты: иконка по типу события трекера, заголовок, текст и время.
 *
 * Ссылка показана отдельной строкой, а не только кликом по карточке: иначе
 * непонятно, уведёт ли клик куда-то вообще (в CRM это добавляли отдельно —
 * DEV-1946).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { AtSign, Bell, CalendarClock, MessageSquare, ShieldCheck, UserPlus } from 'lucide-vue-next'

import { notificationUrl } from './route'
import type { Notification } from './types'

const props = defineProps<{ notification: Notification }>()

const emit = defineEmits<{ click: [notification: Notification] }>()

const { t } = useI18n()

const icon = computed(() => {
  switch (props.notification.notificationType) {
    case 'tracker_issue_assigned':
      return UserPlus
    case 'tracker_issue_mentioned':
      return AtSign
    case 'tracker_issue_commented':
      return MessageSquare
    case 'tracker_access_requested':
      return ShieldCheck
    case 'tracker_meeting_invited':
      return CalendarClock
    default:
      return Bell
  }
})

const hasTarget = computed(() => Boolean(notificationUrl(props.notification)))

const time = computed(() => {
  const raw = props.notification.createdAt
  if (!raw) return ''

  const date = new Date(raw)
  const diffMs = Date.now() - date.getTime()
  const minutes = Math.floor(diffMs / 60_000)
  const hours = Math.floor(diffMs / 3_600_000)
  const days = Math.floor(diffMs / 86_400_000)

  if (minutes < 1) return t('notifications.time.justNow')
  if (minutes < 60) return t('notifications.time.minutesAgo', { n: minutes })
  if (hours < 24) return t('notifications.time.hoursAgo', { n: hours })
  if (days < 7) return t('notifications.time.daysAgo', { n: days })

  return date.toLocaleDateString()
})
</script>

<template>
  <button
    type="button"
    class="item"
    :class="{ 'item--unread': !notification.isRead }"
    @click="emit('click', notification)"
  >
    <span class="item__dot" :class="{ 'item__dot--on': !notification.isRead }" />

    <span class="item__icon">
      <component :is="icon" :size="16" />
    </span>

    <span class="item__content">
      <span class="item__title">{{ notification.title }}</span>
      <span v-if="notification.body" class="item__body">{{ notification.body }}</span>
      <span class="item__meta">
        <span class="item__time">{{ time }}</span>
        <span v-if="hasTarget" class="item__open">{{ t('notifications.open') }}</span>
      </span>
    </span>
  </button>
</template>

<style scoped>
.item {
  display: flex;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid var(--color-border);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.item:hover {
  background-color: var(--color-bg-muted);
}

.item--unread {
  background-color: var(--color-primary-tint);
}

.item__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
}

.item__dot--on {
  background-color: var(--color-primary);
}

.item__icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background-color: var(--color-bg-app);
  color: var(--color-text-muted);
}

.item__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.item__title {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: 500;
}

.item__body {
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item__meta {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 2px;
}

.item__time {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.item__open {
  color: var(--color-primary);
  font-size: var(--font-size-caption);
}
</style>
